import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { authService } from '@/services/authService';
import { ApiError } from '@/lib/api';

const resetSchema = z
  .object({
    new_password: z.string().min(8, { message: 'auth.password_min' }).max(128),
    confirm_password: z.string(),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: 'auth.passwords_dont_match',
    path: ['confirm_password'],
  });

type ResetFormValues = z.infer<typeof resetSchema>;

const REDIRECT_DELAY_MS = 2000;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const token = searchParams.get('token') || '';

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  useEffect(() => {
    if (!token) navigate('/forgot-password', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  async function onSubmit(values: ResetFormValues) {
    setErrorKey(null);
    try {
      await authService.confirmPasswordReset(token, values.new_password);
      setSuccess(true);
      redirectTimerRef.current = setTimeout(() => {
        navigate('/login', { replace: true, state: { flash: 'password_reset' } });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 410) setErrorKey('auth.reset_expired');
        else if (err.status === 400) setErrorKey('auth.reset_invalid');
        else setErrorKey('common.error_generic');
      } else {
        setErrorKey('common.error_generic');
      }
    }
  }

  if (success) {
    return (
      <AuthShell title={t('auth.reset_success')}>
        <p className="text-center text-sm text-muted-foreground">{t('common.loading')}…</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.reset_title')} subtitle={t('auth.reset_subtitle')}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="new_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.new_password')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirm_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.confirm_password')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {errorKey && (
            <div role="alert" className="text-sm text-destructive">
              {t(errorKey)}
            </div>
          )}

          <Button type="submit" className="w-full h-11" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? t('common.loading') : t('auth.reset_button')}
          </Button>

          <p className="text-center text-sm">
            <Link
              to="/login"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('common.back')}
            </Link>
          </p>
        </form>
      </Form>
    </AuthShell>
  );
}
